/**
 * client/src/lib/use-handshake-push.ts
 *
 * Guardian-duty WebSocket push listener (PR A, Task A3) — the in-app half of
 * telling a guardian that a ward needs their approval RIGHT NOW, not only if
 * they happen to open Invitations.
 *
 * Listens for `handshake:guardian_request` — broadcast to each guardian slot
 * by `initiateHandshake` (server/logic/guardian/handshake.ts) via
 * `broadcastToUser`, and already classified `duty` in
 * `server/websocket/ws-event-policy.ts` so a guardian-only shell (ledger #16)
 * receives it too.
 *
 * Modelled directly on `use-invitation-push.ts` (Reuse-First Check,
 * DEVELOPMENT/quality.md Code Review Criteria #8): the same `{type:"auth",
 * userId, sessionId}` handshake, because the server fan-out here is also
 * `broadcastToUser`, not the `dashboard:<orgId>` channel-subscribe
 * vocabulary.
 *
 * REACH IS NOT GUARANTEED — a WebSocket notice reaches a LIVE session only.
 * The Invitations page's "Guardian approvals" section (a plain authenticated
 * read of `GET /api/circle/handshake/pending-duties`) is the load-bearing
 * discovery path; this listener is defence in depth over it, exactly as
 * `use-invitation-push.ts` states for its own rail. No copy anywhere may
 * present this push as the delivery mechanism.
 *
 * This is a notice of an already-committed handshake initiation only — never
 * proof that anyone is a live human (Friction Rule clause 2).
 */

import { useCurrentUser } from "@/lib/use-current-user";
import { useReconnectingWs, type UseReconnectingWsResult } from "@/lib/use-reconnecting-ws";
import { guardianOperationLabel } from "@/lib/terminology";

export interface HandshakeGuardianRequestFrame extends Record<string, unknown> {
  type: "handshake:guardian_request";
  handshakeId: string;
  operationType: string;
  initiatorDisplayName?: string;
  expiresAt: string;
}

/**
 * Listens for `handshake:guardian_request` pushes addressed to the current
 * authenticated user. A null/loading current user opens no socket at all
 * (readiness-gate contract, same as `useInvitationPush`).
 */
export function useHandshakePush(
  onEvent: (frame: HandshakeGuardianRequestFrame) => void,
): UseReconnectingWsResult {
  const { user } = useCurrentUser();

  return useReconnectingWs<HandshakeGuardianRequestFrame>(
    () =>
      user?.id
        ? { type: "auth", userId: user.id, sessionId: `handshake-push-${user.id}` }
        : null,
    (msg) => msg.type === "handshake:guardian_request",
    onEvent,
    user?.id,
  );
}

/**
 * Plain-English, honest one-line summary — states only that an approval is
 * needed, never that anything has taken effect (nothing has until the
 * guardian responds). The server writes the literal string "Unknown" when it
 * cannot resolve a display name (mirrors `lift-status.ts`'s `displayName`
 * fallback), so that value is treated as "not resolved" rather than rendered
 * as a name.
 *
 * Returns null (skip the toast) rather than throwing when `operationType` is
 * missing or not a string — a malformed/unexpected WS frame must never crash
 * the listener; `guardianOperationLabel` assumes a string input.
 */
export function describeHandshakeDuty(frame: HandshakeGuardianRequestFrame): string | null {
  if (typeof frame.operationType !== "string") return null;
  const from = frame.initiatorDisplayName;
  const who = !from || from === "Unknown" ? "Someone you protect" : from;
  return `${who} needs your approval — ${guardianOperationLabel(frame.operationType)}`;
}
